import { useCallback, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";

export default function LocationMaster() {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateId, setStateId] = useState("");
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", state_id: "" });
  const [saving, setSaving] = useState(false);

  const loadStates = useCallback(async () => {
    const result = await adminPost("STATE_LIST");
    if (result.status === "200") {
      const rows = result.data?.rows || [];
      setStates(rows);
      setStateId((current) => current || rows.find((row) => row.status === "1")?.id || rows[0]?.id || "");
    }
  }, []);

  const loadCities = useCallback(async (id) => {
    if (!id) { setCities([]); return; }
    const result = await adminPost("CITY_LIST", { state_id: id });
    if (result.status === "200") setCities(result.data?.rows || []);
  }, []);

  useEffect(() => { loadStates(); }, [loadStates]);
  useEffect(() => { loadCities(stateId); }, [stateId, loadCities]);

  const save = async () => {
    setSaving(true);
    try {
      const mod = open === "state" ? "STATE_SAVE" : "CITY_SAVE";
      const result = await adminPost(mod, open === "city" ? { ...form, state_id: form.state_id || stateId } : form);
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        setOpen(null);
        await loadStates();
        loadCities(form.state_id || stateId);
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (kind, row) => {
    const result = await adminPost(kind === "state" ? "STATE_STATUS" : "CITY_STATUS", { id: row.id, status: row.status === "1" ? "0" : "1" });
    if (result.status === "200") {
      toast.success(result.data?.message);
      loadStates();
      loadCities(stateId);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700 }}>States</Typography>
          <Button size="small" sx={primaryButtonSx} onClick={() => { setForm({ id: "", name: "", state_id: "" }); setOpen("state"); }}>Add state</Button>
        </Box>
        <Box sx={{ display: "grid", gap: 1 }}>
          {states.map((row) => (
            <Box key={row.id} onClick={() => setStateId(row.id)} sx={{ background: stateId === row.id ? "#eff6ff" : "#fff", border: `1px solid ${stateId === row.id ? "#93c5fd" : consoleColors.line}`, borderRadius: 2, p: 1.4, cursor: "pointer" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 650 }}>{row.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: consoleColors.muted }}>{row.city_count || 0} cities · {row.status === "1" ? "Enabled" : "Disabled"}</Typography>
                </Box>
                <Button size="small" sx={{ textTransform: "none" }} onClick={(e) => { e.stopPropagation(); setForm({ id: row.id, name: row.name, state_id: "" }); setOpen("state"); }}>Edit</Button>
                <Button size="small" sx={{ textTransform: "none" }} onClick={(e) => { e.stopPropagation(); toggle("state", row); }}>{row.status === "1" ? "Disable" : "Enable"}</Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700 }}>Cities{states.find((row) => row.id === stateId) ? ` · ${states.find((row) => row.id === stateId).name}` : ""}</Typography>
          <Button size="small" sx={primaryButtonSx} disabled={!stateId} onClick={() => { setForm({ id: "", name: "", state_id: stateId }); setOpen("city"); }}>Add city</Button>
        </Box>
        {cities.length === 0 && <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>No cities for this state.</Typography>}
        <Box sx={{ display: "grid", gap: 1 }}>
          {cities.map((row) => (
            <Box key={row.id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 1.4, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ flex: 1, fontWeight: 650 }}>{row.name}</Typography>
              <Typography sx={{ fontSize: 12, color: consoleColors.muted }}>{row.status === "1" ? "Enabled" : "Disabled"}</Typography>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => { setForm({ id: row.id, name: row.name, state_id: row.state_id }); setOpen("city"); }}>Edit</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => toggle("city", row)}>{row.status === "1" ? "Disable" : "Enable"}</Button>
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog open={Boolean(open)} onClose={() => setOpen(null)} fullWidth maxWidth="xs">
        <DialogTitle>{form.id ? "Edit" : "Add"} {open === "state" ? "state" : "city"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1.5 }}>
          {open === "city" && (
            <TextField select label="State" value={form.state_id} onChange={(e) => setForm({ ...form, state_id: e.target.value })} sx={{ mt: 1 }}>
              {states.filter((row) => row.status === "1").map((row) => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
            </TextField>
          )}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mt: open === "state" ? 1 : 0 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
